/** Helpers for in-app turn-by-turn navigation (ported from go7). */

import { distanceMeters, type LatLng } from './locationSmoothing';

export type NavStep = {
  index: number;
  instruction: string;
  distanceText: string;
  distanceMeters: number;
  durationText: string;
  start: LatLng | null;
  end: LatLng | null;
  maneuver: string;
};

/** Rider-facing turn card */
export type TurnGuidance = {
  icon: string;
  distanceLabel: string;
  /** Short action: Turn right, Continue, etc. */
  action: string;
  /** Street / place if known */
  street: string;
  /** Full readable line */
  detail: string;
  /** Preview of the following turn */
  thenLabel: string | null;
  /** Within ~40 m of maneuver */
  isImminent?: boolean;
};

export function bearingDegrees(from: LatLng, to: LatLng): number {
  const lat1 = (from.lat * Math.PI) / 180;
  const lat2 = (to.lat * Math.PI) / 180;
  const dLng = ((to.lng - from.lng) * Math.PI) / 180;
  const y = Math.sin(dLng) * Math.cos(lat2);
  const x =
    Math.cos(lat1) * Math.sin(lat2) -
    Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

export function compassLabel(bearing: number): string {
  const dirs = ['North', 'Northeast', 'East', 'Southeast', 'South', 'Southwest', 'West', 'Northwest'];
  const idx = Math.round((((bearing % 360) + 360) % 360) / 45) % 8;
  return dirs[idx];
}

function latLngFromGoogle(loc: any): LatLng | null {
  if (!loc) return null;
  if (typeof loc.lat === 'function') {
    return { lat: loc.lat(), lng: loc.lng() };
  }
  return { lat: Number(loc.lat), lng: Number(loc.lng) };
}

function stripHtml(html: string): string {
  return String(html)
    .replace(/<div[^>]*>/gi, ' ')
    .replace(/<\/div>/gi, ' ')
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Infer maneuver when Google omits it (common on "Head …" steps). */
function inferManeuver(raw: string, existing: string): string {
  if (existing) return existing;
  const t = raw.toLowerCase();
  if (/\bu-?turn\b/.test(t)) return 'uturn-left';
  if (/\bturn\s+sharp\s+left\b/.test(t)) return 'turn-sharp-left';
  if (/\bturn\s+sharp\s+right\b/.test(t)) return 'turn-sharp-right';
  if (/\bturn\s+slight\s+left\b|\bbear\s+left\b/.test(t)) return 'turn-slight-left';
  if (/\bturn\s+slight\s+right\b|\bbear\s+right\b/.test(t)) return 'turn-slight-right';
  if (/\bturn\s+left\b/.test(t)) return 'turn-left';
  if (/\bturn\s+right\b/.test(t)) return 'turn-right';
  if (/\broundabout\b/.test(t)) return 'roundabout-left';
  if (/\bmerge\b/.test(t)) return 'merge';
  if (/\bkeep\s+left\b/.test(t)) return 'keep-left';
  if (/\bkeep\s+right\b/.test(t)) return 'keep-right';
  if (/\bcontinue\b|\bhead\b|\bstraight\b/.test(t)) return 'straight';
  return 'straight';
}

export function extractRouteSteps(directions: any): NavStep[] {
  const leg = directions?.routes?.[0]?.legs?.[0];
  if (!leg?.steps?.length) return [];
  return leg.steps.map((step: any, index: number) => {
    const instruction = stripHtml(step.instructions || '');
    const maneuver = inferManeuver(instruction, step.maneuver || '');
    return {
      index,
      instruction,
      distanceText: step.distance?.text || '',
      distanceMeters: step.distance?.value || 0,
      durationText: step.duration?.text || '',
      start: latLngFromGoogle(step.start_location),
      end: latLngFromGoogle(step.end_location),
      maneuver,
    };
  });
}

/** Pick the step the rider should follow next. */
export function findActiveStepIndex(steps: NavStep[], riderLocation: LatLng | null | undefined): number {
  if (!steps?.length || !riderLocation) return 0;
  for (let i = 0; i < steps.length; i += 1) {
    if (!steps[i].end) continue;
    const distToEnd = distanceMeters(riderLocation, steps[i].end!);
    if (distToEnd > 35) return i;
  }
  return Math.max(0, steps.length - 1);
}

export function formatStepDistance(meters: number): string {
  if (!Number.isFinite(meters)) return '';
  if (meters < 20) return `${Math.max(1, Math.round(meters))} m`;
  if (meters < 1000) return `${Math.round(meters / 10) * 10} m`;
  return `${(meters / 1000).toFixed(1)} km`;
}

export function maneuverIcon(maneuver: string): string {
  const m = String(maneuver || '').toLowerCase();
  if (m.includes('left') && m.includes('sharp')) return '↰';
  if (m.includes('right') && m.includes('sharp')) return '↱';
  if (m.includes('left') && (m.includes('slight') || m.includes('keep'))) return '↖';
  if (m.includes('right') && (m.includes('slight') || m.includes('keep'))) return '↗';
  if (m.includes('left')) return '↰';
  if (m.includes('right')) return '↱';
  if (m.includes('uturn') || m.includes('u-turn')) return '↩';
  if (m.includes('roundabout')) return '⟳';
  if (m.includes('merge')) return '⤴';
  if (m.includes('straight') || m.includes('continue') || m.includes('head')) return '↑';
  return '↑';
}

export function maneuverAction(maneuver: string, instruction = ''): string {
  const m = String(maneuver || '').toLowerCase();
  const t = instruction.toLowerCase();
  if (m.includes('uturn') || m.includes('u-turn')) return 'Make a U-turn';
  if (m.includes('roundabout')) return 'Enter roundabout';
  if (m.includes('sharp') && m.includes('left')) return 'Sharp left';
  if (m.includes('sharp') && m.includes('right')) return 'Sharp right';
  if ((m.includes('slight') || m.includes('keep')) && m.includes('left')) return 'Keep left';
  if ((m.includes('slight') || m.includes('keep')) && m.includes('right')) return 'Keep right';
  if (m.includes('left')) return 'Turn left';
  if (m.includes('right')) return 'Turn right';
  if (m.includes('merge')) return 'Merge';
  if (/\bhead\b/.test(t) || m.includes('straight') || m.includes('continue') || !m) {
    return 'Continue';
  }
  return 'Continue';
}

/** Pull street / place name from Google instruction text. */
export function extractStreetName(instruction: string): string {
  const text = stripHtml(instruction);
  if (!text) return '';

  const onto = text.match(/\bonto\s+(.+?)(?:\s+toward\s+|\s+Destination|\s*$)/i);
  if (onto?.[1]) return onto[1].replace(/\.$/, '').trim();

  const on = text.match(/\bon\s+(.+?)(?:\s+toward\s+|\s+Destination|\s*$)/i);
  if (on?.[1]) {
    const street = on[1].replace(/\.$/, '').trim();
    // Avoid "on the left" style noise
    if (!/^(the\s+)?(left|right|ramp|road)$/i.test(street) && street.length > 2) {
      return street;
    }
  }

  const toward = text.match(/\btoward\s+(.+?)(?:\s*$)/i);
  if (toward?.[1]) return toward[1].replace(/\.$/, '').trim();

  return '';
}

/** Clean Google "Head <b>northwest</b> on …" into readable rider guidance. */
export function humanizeStep(step: NavStep | null | undefined): { action: string; street: string; detail: string } {
  if (!step) return { action: 'Follow route', street: '', detail: 'Follow the red line on the map' };

  const action = maneuverAction(step.maneuver, step.instruction);
  const street = extractStreetName(step.instruction);
  let detail = stripHtml(step.instruction);

  // Fix truncated / useless "Head" alone
  if (!detail || detail.length < 5 || /^head$/i.test(detail)) {
    detail = street ? `${action} on ${street}` : action;
  } else if (/^head\b/i.test(detail)) {
    // "Head northwest on X toward Y" → "Continue on X"
    detail = street ? `Continue on ${street}` : detail.replace(/^head\s+\w+\s+/i, 'Continue ');
  }

  return { action, street, detail };
}

export function buildTurnGuidance(
  step: NavStep | null,
  nextStep: NavStep | null,
  distanceToTurnM: number | null
): TurnGuidance {
  const { action, street, detail } = humanizeStep(step);
  const nearTurn =
    distanceToTurnM != null && Number.isFinite(distanceToTurnM) && distanceToTurnM < 40;
  const dist =
    distanceToTurnM != null && Number.isFinite(distanceToTurnM)
      ? formatStepDistance(Math.max(0, distanceToTurnM))
      : step?.distanceText || '';

  let thenLabel: string | null = null;
  if (nextStep && !nearTurn) {
    const next = humanizeStep(nextStep);
    thenLabel = next.street
      ? `Then ${next.action.toLowerCase()} onto ${next.street}`
      : `Then ${next.action.toLowerCase()}`;
  } else if (nextStep && nearTurn) {
    const next = humanizeStep(nextStep);
    thenLabel = next.street ? `Then: ${next.action} · ${next.street}` : `Then: ${next.action}`;
  }

  return {
    icon: maneuverIcon(step?.maneuver || 'straight'),
    distanceLabel: dist,
    action,
    street,
    detail: street ? `${action} onto ${street}` : detail,
    thenLabel,
    isImminent: nearTurn,
  };
}

/** Simple helper when Directions steps are missing (Mapbox fallback). */
export function buildCompassGuidance(
  from: LatLng,
  to: LatLng,
  remainingLabel?: string | null
): TurnGuidance {
  const bearing = bearingDegrees(from, to);
  const dir = compassLabel(bearing);
  const distM = distanceMeters(from, to);
  return {
    icon: '↑',
    distanceLabel: remainingLabel || formatStepDistance(distM),
    action: `Head ${dir}`,
    street: '',
    detail: `Follow the red route · ${dir}`,
    thenLabel: null,
  };
}
